import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, EyeOff, Image as ImageIcon, ArrowRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { safeLocal } from "../lib/safeStorage";
import { PRIVACY_INTRO_SEEN_KEY } from "../lib/storageKeys";

const STORAGE_KEY = PRIVACY_INTRO_SEEN_KEY;

const Dot = ({ active }) => (
  <span
    className={`h-1.5 rounded-full transition-all ${
      active ? "w-8 bg-[#D5A8A4]" : "w-1.5 bg-white/30"
    }`}
  />
);

/**
 * Title with an italic-emphasis word in the middle, sourced from i18n.
 * We split into before/emphasis/after so translators can re-order the
 * emphasized word naturally (FR: "Tes données sont chiffrées dès…",
 * ES: "Tus datos están cifrados desde…") without us doing string surgery.
 */
const SlideTitle = ({ before, emphasis, after }) => (
  <>
    {before}{" "}
    <span className="font-serif italic text-[#D5A8A4]">{emphasis}</span>{" "}
    {after}
  </>
);

const PrivacyIntro = ({ forceOpen = null, onClose }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  // Slide definitions are derived from i18n inside the component so a
  // mid-session language switch (Settings → Language) immediately
  // re-renders the modal in the new locale.
  const slides = useMemo(
    () => [
      {
        icon: Lock,
        eyebrow: t("privacyIntro.slide1Eyebrow"),
        title: (
          <SlideTitle
            before={t("privacyIntro.slide1TitleBefore")}
            emphasis={t("privacyIntro.slide1TitleEmphasis")}
            after={t("privacyIntro.slide1TitleAfter")}
          />
        ),
        body: t("privacyIntro.slide1Body"),
      },
      {
        icon: ImageIcon,
        eyebrow: t("privacyIntro.slide2Eyebrow"),
        title: (
          <SlideTitle
            before={t("privacyIntro.slide2TitleBefore")}
            emphasis={t("privacyIntro.slide2TitleEmphasis")}
            after={t("privacyIntro.slide2TitleAfter")}
          />
        ),
        body: t("privacyIntro.slide2Body"),
      },
      {
        icon: EyeOff,
        eyebrow: t("privacyIntro.slide3Eyebrow"),
        title: (
          <SlideTitle
            before={t("privacyIntro.slide3TitleBefore")}
            emphasis={t("privacyIntro.slide3TitleEmphasis")}
            after={t("privacyIntro.slide3TitleAfter")}
          />
        ),
        body: t("privacyIntro.slide3Body"),
      },
    ],
    [t],
  );

  // PrivacyIntro is now intent-gated. It opens ONLY when a parent
  // explicitly sets forceOpen=true (the "Create your free account"
  // signup CTA on Welcome, or the "How we protect you" educational
  // link). Cold ad traffic landing on `/` no longer gets a wall of
  // privacy copy before they understand what TruBeauté does.
  // The "seen once" localStorage flag still applies — once a user
  // has been through the modal once, future signup-CTA clicks
  // navigate straight to /login without re-prompting.
  useEffect(() => {
    if (forceOpen === true) {
      // Already seen? Skip the modal entirely and let the parent's
      // onClose fire (so the navigation that depends on this modal
      // dismissing still proceeds).
      if (safeLocal.getItem(STORAGE_KEY) === "1") {
        onClose?.();
        return;
      }
      setOpen(true);
      setIdx(0);
      return;
    }
    if (forceOpen === false) {
      setOpen(false);
      return;
    }
    // forceOpen === null (default) → never auto-open. The modal
    // is now strictly intent-gated; first-paint stays clean.
  }, [forceOpen, onClose]);

  const dismiss = () => {
    safeLocal.setItem(STORAGE_KEY, "1");
    setOpen(false);
    onClose?.();
  };

  const next = () => {
    if (idx < slides.length - 1) setIdx(idx + 1);
    else dismiss();
  };

  if (!open) return null;
  const slide = slides[idx];
  const Icon = slide.icon;

  return (
    <AnimatePresence>
      <motion.div
        key="privacy-intro-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#1a1416]/70 backdrop-blur-md px-4 pb-4 sm:pb-0"
        data-testid="privacy-intro"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md bg-[#2C2123] text-[#FAF6F2] rounded-3xl p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden"
        >
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-[#D5A8A4]/60 hover:text-[#D5A8A4] transition-colors"
            aria-label={t("privacyIntro.skipLabel")}
            data-testid="privacy-intro-skip"
          >
            <X className="h-5 w-5" />
          </button>

          {/* soft halo */}
          <div className="absolute -top-24 -right-16 h-56 w-56 rounded-full bg-[#9B6A6C]/30 blur-3xl pointer-events-none" />

          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.35 }}
              className="relative"
            >
              <div className="h-12 w-12 rounded-full bg-[#3a2b2e] flex items-center justify-center mb-5">
                <Icon className="h-5 w-5 text-[#D5A8A4]" />
              </div>
              <span className="block text-[11px] uppercase tracking-[0.28em] text-[#D5A8A4] mb-3">
                {slide.eyebrow}
              </span>
              <h2
                className="text-3xl leading-tight font-light mb-4"
                data-testid={`privacy-intro-title-${idx}`}
              >
                {slide.title}
              </h2>
              <p className="text-[#E5D6D2] leading-relaxed text-[15px]">
                {slide.body}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2" data-testid="privacy-intro-dots">
              {slides.map((s, i) => (
                <Dot key={s.eyebrow} active={i === idx} />
              ))}
            </div>
            <button
              onClick={next}
              className="inline-flex items-center gap-2 bg-[#D5A8A4] hover:bg-[#E5C0BC] text-[#2C2123] font-medium rounded-full px-5 py-2.5 transition-all"
              data-testid="privacy-intro-next"
            >
              {idx === slides.length - 1
                ? t("privacyIntro.begin")
                : t("privacyIntro.next")}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrivacyIntro;
